'use client'

import { cn } from '@brew/ui/cn'
import { Check, ChevronRight, Copy, FileCode2, Upload } from 'lucide-react'
import {
  type ChangeEvent,
  type Dispatch,
  type DragEvent,
  useCallback,
  useId,
  useState,
} from 'react'
import { homeButtonClasses } from '@/components/marketing/home/home-button'
import {
  MK_BODY,
  MK_CAPTION,
  MK_LABEL,
  MK_SMALL,
} from '@/components/marketing/home/home-layout'
import { EMAIL_AUDIT_COPY_FIELD_MAX_LENGTH } from '@/lib/email/audit/contracts'
import { trackFreeToolEvent } from '@/lib/site/gtm'
import { useFormatAuditBytes } from './email-audit-format'
import {
  type EmailAuditToolAction,
  type EmailAuditToolState,
  emailAuditFileProblem,
  nextEmailAuditLoad,
} from './email-audit-tool-state'
import { useCopyToClipboard } from './use-copy-to-clipboard'
import { useEmailAuditCopy } from './use-email-audit-copy'

/** The input card's panels: paste, upload, the envelope and send-by-email. */

const FIELD =
  'w-full rounded-xl bg-mk-tile px-3.5 text-mk-ink outline-none ring-1 ring-mk-line transition-shadow duration-quick ease-smooth-out placeholder:text-mk-faint focus:ring-2 focus:ring-mk-accent'

export function PastePanel({
  html,
  dispatch,
}: {
  html: string
  dispatch: Dispatch<EmailAuditToolAction>
}) {
  const id = useId()
  const copy = useEmailAuditCopy()
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) =>
      dispatch({ type: 'html', html: event.currentTarget.value }),
    [dispatch]
  )
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="sr-only">
        {copy.form.htmlLabel}
      </label>
      <textarea
        id={id}
        value={html}
        onChange={handleChange}
        spellCheck={false}
        autoCapitalize="none"
        autoComplete="off"
        placeholder={copy.form.htmlPlaceholder}
        className={cn(
          MK_CAPTION,
          'block h-72 w-full resize-y bg-transparent px-5 py-4 font-mk-mono text-mk-ink outline-none placeholder:text-mk-faint sm:px-6'
        )}
      />
    </div>
  )
}

export function UploadPanel({
  file,
  dispatch,
}: {
  file: EmailAuditToolState['file']
  dispatch: Dispatch<EmailAuditToolAction>
}) {
  const id = useId()
  const copy = useEmailAuditCopy()
  const formatBytes = useFormatAuditBytes()
  const [isDragging, setIsDragging] = useState(false)

  const readFile = useCallback(
    async (chosen: File | undefined) => {
      if (!chosen) {
        return
      }
      // Choosing a file replaces any load still in flight (a sample, an
      // earlier file), even when this one is refused.
      const load = nextEmailAuditLoad()
      dispatch({ type: 'loadStarted', load })
      const problem = emailAuditFileProblem(chosen)
      if (problem) {
        dispatch({ type: 'loadFailed', load, message: copy.form[problem] })
        return
      }
      let html: string
      try {
        html = await chosen.text()
      } catch {
        dispatch({
          type: 'loadFailed',
          load,
          message: copy.form.fileUnreadable,
        })
        return
      }
      dispatch({ type: 'file', load, name: chosen.name, html })
    },
    [copy, dispatch]
  )

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      void readFile(event.currentTarget.files?.[0])
      event.currentTarget.value = ''
    },
    [readFile]
  )

  const handleDragOver = useCallback((event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback(() => setIsDragging(false), [])

  const handleDrop = useCallback(
    (event: DragEvent<HTMLLabelElement>) => {
      event.preventDefault()
      setIsDragging(false)
      void readFile(event.dataTransfer.files[0])
    },
    [readFile]
  )

  return (
    <label
      htmlFor={id}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'm-4 flex h-64 cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-mk-line border-dashed bg-mk-tile px-6 text-center transition-colors duration-quick ease-smooth-out hover:border-mk-accent/60 sm:m-5',
        { 'border-mk-accent bg-mk-card': isDragging }
      )}
    >
      <input
        id={id}
        type="file"
        accept=".html,.htm,text/html"
        onChange={handleChange}
        className="sr-only"
      />
      <span className="grid size-10 place-items-center rounded-full bg-mk-card shadow-mk-card ring-1 ring-mk-line">
        {file ? (
          <FileCode2 aria-hidden="true" className="size-4 text-mk-accent" />
        ) : (
          <Upload aria-hidden="true" className="size-4 text-mk-accent" />
        )}
      </span>
      {file ? (
        <span className="flex flex-col gap-0.5">
          <span className={cn(MK_LABEL, 'break-all text-mk-ink')}>
            {file.name}
          </span>
          <span className={cn(MK_CAPTION, 'text-mk-muted')}>
            {formatBytes(file.bytes)} · {copy.form.dropReplace}
          </span>
        </span>
      ) : (
        <span className="flex flex-col gap-0.5">
          <span className={cn(MK_LABEL, 'text-mk-ink')}>
            {copy.form.dropTitle}
          </span>
          <span className={cn(MK_SMALL, 'text-mk-muted')}>
            {copy.form.dropBrowse}
          </span>
        </span>
      )}
    </label>
  )
}

export function EnvelopeFields({
  subject,
  previewText,
  dispatch,
}: {
  subject: string
  previewText: string
  dispatch: Dispatch<EmailAuditToolAction>
}) {
  const subjectId = useId()
  const previewId = useId()
  const copy = useEmailAuditCopy()
  const handleSubject = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      dispatch({ type: 'subject', subject: event.currentTarget.value }),
    [dispatch]
  )
  const handlePreview = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      dispatch({ type: 'previewText', previewText: event.currentTarget.value }),
    [dispatch]
  )
  const summary = subject.trim()
    ? copy.form.subjectSummary(subject.trim())
    : copy.form.envelopeToggle
  return (
    <details className="group border-mk-card-line border-t">
      <summary
        className={cn(
          MK_CAPTION,
          'flex cursor-pointer list-none items-center gap-2 px-5 py-3 text-mk-ink-2 hover:text-mk-ink sm:px-6 [&::-webkit-details-marker]:hidden'
        )}
      >
        <ChevronRight
          aria-hidden="true"
          className="size-3.5 shrink-0 transition-transform duration-quick ease-smooth-out group-open:rotate-90"
        />
        <span className="truncate">{summary}</span>
      </summary>
      <div className="grid gap-3 px-5 pb-4 sm:grid-cols-2 sm:px-6">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={subjectId}
            className={cn(MK_CAPTION, 'text-mk-ink-2')}
          >
            {copy.form.subjectLabel}
          </label>
          <input
            id={subjectId}
            value={subject}
            onChange={handleSubject}
            maxLength={EMAIL_AUDIT_COPY_FIELD_MAX_LENGTH}
            placeholder={copy.form.subjectPlaceholder}
            className={cn(FIELD, MK_SMALL, 'h-10')}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={previewId}
            className={cn(MK_CAPTION, 'text-mk-ink-2')}
          >
            {copy.form.previewLabel}
          </label>
          <input
            id={previewId}
            value={previewText}
            onChange={handlePreview}
            maxLength={EMAIL_AUDIT_COPY_FIELD_MAX_LENGTH}
            placeholder={copy.form.previewPlaceholder}
            className={cn(FIELD, MK_SMALL, 'h-10')}
          />
        </div>
      </div>
    </details>
  )
}

export function ByEmailPanel() {
  const { isCopied, copy: copyText } = useCopyToClipboard()
  const copy = useEmailAuditCopy()
  const handleCopy = useCallback(async () => {
    if (await copyText(copy.byEmail.address)) {
      trackFreeToolEvent({ tool: 'email-audit', action: 'copy_address' })
    }
  }, [copy, copyText])
  return (
    <div className="flex min-h-72 flex-col justify-center gap-5 p-5 sm:p-6">
      <div className="flex flex-col items-stretch gap-2 rounded-xl bg-mk-tile p-2 ring-1 ring-mk-line sm:flex-row sm:items-center sm:justify-between sm:pl-4">
        <span className={cn(MK_BODY, 'truncate font-mk-mono text-mk-ink')}>
          {copy.byEmail.address}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className={homeButtonClasses({ size: 'sm', className: 'gap-1.5' })}
        >
          {isCopied ? (
            <Check aria-hidden="true" className="size-3.5" />
          ) : (
            <Copy aria-hidden="true" className="size-3.5" />
          )}
          {isCopied ? copy.byEmail.copied : copy.byEmail.copy}
        </button>
      </div>
      <ol className="flex flex-col gap-3">
        {copy.byEmail.steps.map((step, index) => (
          <li key={step} className="flex items-start gap-3">
            <span
              className={cn(
                MK_CAPTION,
                'grid size-6 shrink-0 place-items-center rounded-full bg-mk-card font-medium text-mk-accent tabular-nums ring-1 ring-mk-line'
              )}
            >
              {index + 1}
            </span>
            <span className={cn(MK_SMALL, 'text-mk-ink-2')}>{step}</span>
          </li>
        ))}
      </ol>
      <p className={cn(MK_CAPTION, 'text-mk-muted')}>{copy.byEmail.note}</p>
    </div>
  )
}
